<template>
  <div
    v-if="contents"
    ref="mdRoot"
    v-image-modal="enableImageModal"
    class="mark-down-sb"
    :class="{ 'image-modal-enabled': enableImageModal }"
    v-html="sanitizedMarkdown"
  ></div>
</template>

<script>
import MarkdownIt from 'markdown-it';
import DOMPurify from 'dompurify';
import { createTablePaddingMixin } from '@/mixins/tablePaddingMixin';
import { createDiagramScaleMixin } from '@/mixins/diagramScaleMixin';

export default {
  name: 'MdSb',
  mixins: [
    createTablePaddingMixin({ refName: 'mdRoot', contentWatchProp: 'sanitizedMarkdown' }),
    createDiagramScaleMixin({ refName: 'mdRoot', contentWatchProp: 'sanitizedMarkdown' })
  ],
  props: {
    contents: { type: String, default: '' },
    enableImageModal: { type: Boolean, default: false }
  },
  data() {
    return {
      md: new MarkdownIt({
        html: true,
        breaks: true,
        linkify: true
      })
    };
  },
  computed: {
    sanitizedMarkdown() {
      if (!this.contents) return '';

      // 1) 주석 제거
      const withoutComments = this.contents.replace(/<!--[\s\S]*?-->/g, '');

      // 2) markdown / html 처리
      const rawHtml = /<\/?[a-z][\s\S]*>/i.test(withoutComments.trim());
      const rendered = rawHtml ? withoutComments : this.md.render(withoutComments);

      // 3) sanitize
      const clean = DOMPurify.sanitize(rendered, {
        ADD_TAGS: [
          'iframe',
          'svg',
          'path',
          'circle',
          'rect',
          'line',
          'polyline',
          'polygon',
          'ellipse',
          'g',
          'defs',
          'use',
          'symbol',
          'clipPath',
          'mask',
          'pattern',
          'linearGradient',
          'radialGradient',
          'stop',
          'text',
          'tspan',
          'textPath',
          'foreignObject',
          'image',
          'animate',
          'animateTransform'
        ],
        ADD_ATTR: [
          'target',
          'rel',
          'allow',
          'allowfullscreen',
          'frameborder',
          'scrolling',
          'viewBox',
          'xmlns',
          'xmlns:xlink',
          'width',
          'height',
          'fill',
          'stroke',
          'stroke-width',
          'stroke-linecap',
          'stroke-linejoin',
          'stroke-dasharray',
          'stroke-miterlimit',
          'd',
          'cx',
          'cy',
          'r',
          'rx',
          'ry',
          'x',
          'y',
          'x1',
          'x2',
          'y1',
          'y2',
          'points',
          'transform',
          'opacity',
          'fill-opacity',
          'fill-rule',
          'stroke-opacity',
          'gradientUnits',
          'gradientTransform',
          'offset',
          'stop-color',
          'stop-opacity',
          'clip-path',
          'clip-rule',
          'mask',
          'preserveAspectRatio',
          'id',
          'class',
          'style',
          'xlink:href',
          'href',
          'version',
          'color',
          'vector-effect'
        ],
        ALLOW_COMMENTS: false,
        KEEP_CONTENT: true,
        RETURN_DOM: false,
        RETURN_DOM_FRAGMENT: false,
        RETURN_DOM_IMPORT: false,
        FORCE_BODY: false,
        NAMESPACE: 'http://www.w3.org/1999/xhtml'
      });

      // 4) div로 감싸기
      return `<div class="md-wrapper">${clean}</div>`;
    }
  }
};
</script>

<style scoped>
.md-wrapper {
  width: 100%;
  overflow-x: auto;
}

.mark-down-sb.image-modal-enabled ::v-deep img {
  cursor: pointer;
}

::v-deep pre[class*='language-'] {
  background: #f6f8fa;
  color: #24292e;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 0.85rem;
  line-height: 1.6;
  padding: 1rem 1.25rem;
  border-radius: 6px;
  border: 1px solid #e1e4e8;
  margin: 0.75rem 0;
  overflow-x: auto;
  white-space: pre;
  word-wrap: normal;
}

::v-deep pre[class*='language-'] code {
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: inherit;
  color: inherit;
  background: none;
  padding: 0;
}
</style>
